import React, { useState, useEffect, useMemo } from 'react';
import './Schedule.css';

const Schedule = ({ config }) => {
  // Collect all events in order from all days
  const allEvents = useMemo(() => {
    const events = [];
    config.content.schedule.days.forEach(day => {
      day.events.forEach(event => {
        events.push({
          ...event,
          date: day.date
        });
      });
    });
    return events;
  }, [config]);

  const [selectedEventSlug, setSelectedEventSlug] = useState(
    allEvents.length > 0 ? allEvents[0].slug : null
  );
  const [showDetails, setShowDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedEvent = allEvents.find(e => e.slug === selectedEventSlug);

  // Scroll to event details container when showDetails becomes true in mobile view
  useEffect(() => {
    if (isMobile && showDetails && selectedEvent) {
      setTimeout(() => {
        const container = document.querySelector('.schedule-mobile-details');
        if (container) {
          const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isMobile, showDetails, selectedEvent]);

  const handleEventSelect = (slug) => {
    setSelectedEventSlug(slug);
    if (isMobile) {
      setShowDetails(true);
      // Scroll to event details container when event is selected in mobile view
      setTimeout(() => {
        const container = document.querySelector('.schedule-mobile-details');
        if (container) {
          const containerTop = container.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: containerTop - 20, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const handleBackToMenu = () => {
    if (isMobile) {
      setShowDetails(false);
    }
  };

  const getFunDescription = (event) => {
    return config.content.schedule.eventDescriptions[event.slug] || '';
  };

  if (isMobile) {
    if (!showDetails) {
      return (
        <section id="schedule" className="schedule section">
          <div className="schedule-mobile-menu">
            <h2 className="section-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
              {config.content.navigation.events}
            </h2>
            <div className="schedule-menu-list schedule-menu-list-mobile">
              {allEvents.map((event, index) => (
                <div
                  key={index}
                  className={`schedule-menu-item schedule-menu-item-mobile ${selectedEventSlug === event.slug ? 'active' : ''}`}
                  onClick={() => handleEventSelect(event.slug)}
                  style={{
                    fontFamily: config.design.fonts.body,
                    color: selectedEventSlug === event.slug ? config.design.colors.primary : config.design.colors.secondary
                  }}
                >
                  <span className="schedule-menu-arrow">→</span>
                    <div className="schedule-menu-item-content">
                    <div className="schedule-menu-item-title">{event.title}</div>
                    <div className="schedule-menu-item-date">
                      {event.date} • {event.time}
                    </div>
                    {getFunDescription(event) && (
                      <div className="schedule-menu-item-description">
                        {getFunDescription(event)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    } else {
      if (!selectedEvent) return null;

      return (
        <section id="schedule" className="schedule section">
          <div className="schedule-mobile-details">
            <button 
              onClick={handleBackToMenu}
              className="schedule-back-button"
              style={{ 
                fontFamily: config.design.fonts.body,
                color: config.design.colors.primary,
                borderColor: config.design.colors.primary
              }}
            >
              ← Back to {config.content.navigation.events}
            </button>
            <EventContent event={selectedEvent} config={config} />
          </div>
        </section>
      );
    }
  }

  if (!selectedEvent) return null;

  return (
    <section id="schedule" className="schedule section">
      <div className="schedule-layout">
        <div className="schedule-menu-sidebar">
          <h2 className="schedule-events-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
            {config.content.navigation.events}
          </h2>
          <div className="schedule-menu-list">
            {allEvents.map((event, index) => (
              <div
                key={index}
                className={`schedule-menu-item ${selectedEventSlug === event.slug ? 'active' : ''}`}
                onClick={() => handleEventSelect(event.slug)}
                style={{
                  fontFamily: config.design.fonts.body,
                  color: selectedEventSlug === event.slug ? config.design.colors.primary : config.design.colors.secondary,
                  backgroundColor: selectedEventSlug === event.slug ? 'rgba(58, 37, 18, 0.05)' : 'transparent'
                }}
              >
                {event.title}
              </div>
            ))}
          </div>
        </div>

        <div className="schedule-content-wrapper">
          <EventContent event={selectedEvent} config={config} />
        </div>
      </div>
    </section>
  );
};

const EventContent = ({ event, config }) => {
  if (!event) return null;

  return (
    <div className="schedule-event-content">
      <h1 className="schedule-event-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary, marginBottom: event.nickname ? '2px' : '20px' }}>
        {event.title}
      </h1>
      {event.nickname && (
        <div className="schedule-event-nickname" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary, marginTop: '0px', marginBottom: '35px' }}>
          {event.nickname}
        </div>
      )}
      <div className="schedule-event-date" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
        {event.date} • {event.time}
      </div>

      <div className="schedule-event-section">
        <h2 className="schedule-section-heading" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
          About {event.title}
        </h2>
        <div className="schedule-section-content" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
          {event.definition.split('\n').map((line, index) => (
            <p key={index} style={{ marginBottom: line.trim() ? '1rem' : '0.5rem', whiteSpace: 'pre-wrap' }}>
              {line.trim() || '\u00A0'}
            </p>
          ))}
        </div>
      </div>

      {event.expectations && (
        <div className="schedule-event-section">
          <h2 className="schedule-section-heading" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
            What to Expect
          </h2>
          <div className="schedule-section-content" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
            {event.expectations.split('\n').map((line, index) => (
              <p key={index} style={{ marginBottom: line.trim() ? '0.8rem' : '0.3rem', whiteSpace: 'pre-wrap' }}>
                {line.trim() || '\u00A0'}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="schedule-event-section">
        <h2 className="schedule-section-heading" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
          Dress Code
        </h2>
        <div className="schedule-section-content" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
          {event.dressCode.split('\n').map((line, index) => (
            <p key={index} style={{ marginBottom: line.trim() ? '0.8rem' : '0.3rem', whiteSpace: 'pre-wrap' }}>
              {line.trim() || '\u00A0'}
            </p>
          ))}
        </div>
      </div>

      <div className="schedule-event-section">
        <h2 className="schedule-section-heading" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
          Location
        </h2>
        <p className="schedule-section-content" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
          <strong>{config.resort.name}</strong>
        </p>
        <p className="schedule-section-content" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
          {config.resort.address}
        </p>
      </div>
    </div>
  );
};

export default Schedule;

